// Convert RGB to hex
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// With a bounding box, get the average color of the background
// by computing points around the bounding box
export function getBackgroundColor(
  position: { x: number, y: number, width: number, height: number },
  image: HTMLImageElement
) {
  try {

    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '#ffffff';

    ctx.drawImage(image, 0, 0);
    
    // Sample points around the text block (further away from text)
    const points = [
      { x: position.x - 10, y: position.y - 10 },  // Top left
      { x: position.x + position.width + 10, y: position.y - 10 },  // Top right
      { x: position.x - 10, y: position.y + position.height + 10 },  // Bottom left
      { x: position.x + position.width + 10, y: position.y + position.height + 10 },  // Bottom right
      { x: position.x + position.width/2, y: position.y - 10 },  // Top middle
      { x: position.x + position.width/2, y: position.y + position.height + 10 },  // Bottom middle
    ];

    // Get average color from sampled points
    let r = 0, g = 0, b = 0;
    let validPoints = 0;

    points.forEach(point => {
      const x = Math.max(0, Math.min(point.x, image.width - 1));
      const y = Math.max(0, Math.min(point.y, image.height - 1));
      const imageData = ctx.getImageData(x, y, 1, 1).data;
      r += imageData[0];
      g += imageData[1];
      b += imageData[2];
      validPoints++;
    });

    return rgbToHex(r/validPoints, g/validPoints, b/validPoints);
  } catch (e) {
    console.error('Error getting background color', e);
    return '#ffffff';
  }
}


// With a bounding box, get the darkest color of the text
// by computing points within the bounding box
export function getTextColor(position: { x: number, y: number, width: number, height: number }, image: HTMLImageElement) {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '#000000';

    ctx.drawImage(image, 0, 0);
    
    // Sample points within the text block area
    const points = [
      { x: position.x + position.width * 0.25, y: position.y + position.height * 0.5 },  // Left quarter
      { x: position.x + position.width * 0.5, y: position.y + position.height * 0.5 },   // Middle
      { x: position.x + position.width * 0.75, y: position.y + position.height * 0.5 },  // Right quarter
    ];

    // Get darkest color from sampled points (assuming text is darker than background)
    let minBrightness = 255;
    let textColor = { r: 0, g: 0, b: 0 };

    points.forEach(point => {
      const x = Math.max(0, Math.min(point.x, image.width - 1));
      const y = Math.max(0, Math.min(point.y, image.height - 1));
      const imageData = ctx.getImageData(x, y, 1, 1).data;
      const brightness = (imageData[0] + imageData[1] + imageData[2]) / 3;
      
      if (brightness < minBrightness) {
        minBrightness = brightness;
        textColor = { r: imageData[0], g: imageData[1], b: imageData[2] };
      }
    });

    return rgbToHex(textColor.r, textColor.g, textColor.b);
  } catch (e) {
    console.error('Error getting text color', e);
    return '#000000';
  }
}
